import { useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, type ColumnDef } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { adminApi } from '../../../api/adminApi';
import type { AssignmentCell, AssignmentKey, AssignmentSummary } from '../../../types/assignmentQuality';
import { configLabel, verdictFromScore } from '../../../types/assignmentQuality';
import { assignmentAnalysesPath, questionDetailPath } from './assignmentRoutes';
import { VerdictBadge } from './qualityCells';
import './AssignmentQuality.css';

/** A single number, or an em-dash when there is nothing to show. Absent is not
 *  zero: no analyses and a score of zero are different facts. */
/** Opens a target in a new tab without navigating this one. stopPropagation
 *  because these sit inside rows that navigate on click. */
export function PopOut({ to, label }: { to: string; label: string }) {
  return (
    <a
      className="aq-popout"
      href={to}
      target="_blank"
      rel="noreferrer"
      title={label}
      aria-label={label}
      onClick={e => e.stopPropagation()}
    >
      ↗
    </a>
  );
}

export function MetricCell({ value }: { value?: number }) {
  return value === undefined ? <span className="aq-muted">—</span> : <b>{value}</b>;
}

/** Only the things that went wrong; silence means nothing did. */
export function HealthSummary({ cell }: { cell: AssignmentCell }) {
  const bad = cell.failed > 0 || cell.pending > 0 || cell.thumbsDown > 0 || cell.skipped > 0;
  if (!bad) return <span className="aq-muted">clean</span>;
  return (
    <div className="aq-metric">
      {cell.failed > 0 && <span className="aq-badge bad">{cell.failed} failed</span>}
      {cell.pending > 0 && <span className="aq-badge warn">{cell.pending} pending</span>}
      {cell.thumbsDown > 0 && <span className="aq-badge bad">{cell.thumbsDown} 👎</span>}
      {cell.skipped > 0 && <span className="aq-badge">{cell.skipped} skipped</span>}
    </div>
  );
}

export function CellSummary({ cell }: { cell: AssignmentCell }) {
  if (cell.attempts === 0) return <span className="aq-cell-empty">—</span>;
  return (
    <div className="aq-metric">
      <b>{cell.avgFinalScore ?? '—'}</b>
      <span className="aq-badge">{cell.attempts} analys{cell.attempts === 1 ? 'is' : 'es'}</span>
      {cell.failed > 0 && <span className="aq-badge bad">{cell.failed} failed</span>}
      {cell.pending > 0 && <span className="aq-badge warn">{cell.pending} pending</span>}
      {cell.thumbsDown > 0 && <span className="aq-badge bad">{cell.thumbsDown} 👎</span>}
      {cell.skipped > 0 && <span className="aq-badge">{cell.skipped} skipped</span>}
      {cell.judged > 0 && (
        // The mean of a three-valued scale is not a fourth value — it is shown
        // as the word it rounds to, carrying the count that earned it. Without
        // that count the word sits beside "14 analyses" and reads as describing
        // all 14, when it may be the mean of 3.
        <span className="aq-metric" title={`${cell.judged} of ${cell.attempts} analyses judged`}>
          <VerdictBadge
            value={cell.qualityAccuracy === undefined ? undefined : verdictFromScore(cell.qualityAccuracy)}
            label="acc"
            count={cell.judged}
          />
          <VerdictBadge
            value={cell.qualityApproach === undefined ? undefined : verdictFromScore(cell.qualityApproach)}
            label="app"
            count={cell.judged}
          />
        </span>
      )}
    </div>
  );
}

function AssignmentHeader({ assignment }: { assignment: AssignmentSummary }) {
  return (
    <div className="aq-head">
      <h2>{assignment.title}</h2>
      <p className="aq-sub">
        {assignment.subject}
        {assignment.modality ? ` · ${assignment.modality}` : ''}
        {' · '}assigned by {assignment.assignedByName || assignment.assignedById.slice(0, 12)}
        {' · '}{assignment.students} student{assignment.students === 1 ? '' : 's'}
        {assignment.declined > 0 ? ` (${assignment.declined} declined)` : ''}
        {assignment.dueAt ? ` · due ${assignment.dueAt}` : ''}
      </p>
    </div>
  );
}

/**
 * Promote this assignment into an eval dataset and judge what it shipped.
 *
 * Both steps are idempotent, so the button is safe to press twice: promote
 * dedups on the source submission and the judge skips cells that already carry
 * a verdict. Synchronous — an admin who pressed it wants the count back.
 */
function JudgeAction({ assignmentKey, judged, attempts }: { assignmentKey: AssignmentKey; judged: number; attempts: number }) {
  const qc = useQueryClient();
  // Re-judging is the only case that needs force: the judge skips a cell that
  // already carries a verdict, so without it the button would look pressable
  // and do nothing once everything is judged.
  const unjudged = Math.max(0, attempts - judged);
  const judge = useMutation({
    mutationFn: (force: boolean) => adminApi.judgeAssignment(assignmentKey, force),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assignmentMatrix'] }),
  });

  const label = judge.isPending
    ? 'Judging…'
    : unjudged > 0
      // Say the number: "judge new analyses" leaves the reader guessing whether
      // that is 2 or 200, and it is the whole cost of pressing it.
      ? `Judge ${unjudged} unjudged`
      : 'Re-judge all';

  return (
    <div className="aq-filters">
      <button
        className="btn btn-primary"
        disabled={judge.isPending || attempts === 0}
        title={unjudged > 0
          ? 'Runs the LLM judge over the analyses that have no verdict yet. Already-judged ones are skipped.'
          : 'Every analysis already has a verdict. This re-runs the judge over all of them, replacing them.'}
        onClick={() => judge.mutate(unjudged === 0)}
      >
        {label}
      </button>
      <span className="aq-muted">
        {attempts === 0
          ? 'Nothing to judge yet.'
          : `${judged} of ${attempts} analyses judged.`}
      </span>
      {judge.data && (
        <span className="aq-muted">
          captured {judge.data.captured}, judged {judge.data.judge.cellsJudged} of {judge.data.judge.cellsConsidered} cells
          {judge.data.judge.failedBatches > 0 ? ` (${judge.data.judge.failedBatches} batches failed)` : ''}
        </span>
      )}
      {judge.error && <span className="aq-warn">{(judge.error as Error).message}</span>}
    </div>
  );
}

/**
 * S2 — question rows × operation-config columns.
 *
 * One column is the normal case: in production a student runs under exactly one
 * config, so a second column only appears when an experiment split the class.
 * See docs/FeatureDesigns/Evals/assignment-analysis-quality.md §3.
 */
export default function AssignmentQualityDetail() {
  const navigate = useNavigate();
  const { questionSetId = '' } = useParams();
  const [params] = useSearchParams();
  const key: AssignmentKey = useMemo(() => ({
    questionSetId,
    assignedById: params.get('by') ?? '',
    batchId: params.get('batchId') ?? undefined,
  }), [questionSetId, params]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['assignmentMatrix', key.questionSetId, key.assignedById, key.batchId],
    queryFn: () => adminApi.getAssignmentMatrix(key),
    enabled: !!key.questionSetId && !!key.assignedById,
  });

  if (!key.assignedById) return <div className="error">Missing assigner — open this page from the Assignments list.</div>;
  if (isLoading) return <div className="aq-empty">Loading assignment…</div>;
  if (error) return <div className="error">Error: {(error as Error).message}</div>;
  if (!data) return <div className="aq-empty">Assignment not found.</div>;

  const { configs, questions, students, total } = data;

  // One metric per column. They were packed into one cell as "10 · 3 analyses
  // · 2 failed", which reads as a single verdict when it is four separate
  // measurements — and cannot be sorted on.
  const questionColumns: ColumnDef<(typeof questions)[number]>[] = [
    {
      key: 'question',
      header: 'Question',
      mobile: 'title',
      render: (q) => {
        const index = questions.indexOf(q) + 1;
        // Plain text, not a link: the whole row navigates, and a link inside a
        // clickable row gives two hit targets for one destination.
        return (
          <div className="aq-qcell">
            {index}. <LatexText text={q.questionText || q.questionId} />
            <PopOut to={questionDetailPath(q.questionId)} label="Open this question in a new tab" />
          </div>
        );
      },
    },
    {
      key: 'questionType',
      header: 'Type',
      render: q => <span className="aq-muted">{q.questionType ?? '—'}</span>,
    },
    {
      key: 'analyses',
      header: 'Analyses',
      render: q => <MetricCell value={q.total.attempts || undefined} />,
    },
    {
      key: 'students',
      header: 'Students attempted',
      render: q => <MetricCell value={q.total.students || undefined} />,
    },
    {
      key: 'accuracy',
      header: 'Avg accuracy score',
      render: q => <MetricCell value={q.total.avgCorrectnessScore} />,
    },
    {
      key: 'evalScore',
      header: 'Eval score',
      // Blank until judged — an unjudged question has no eval score, and a 0
      // would read as "judged, and bad".
      render: q => (q.total.judged > 0
        ? <span className="aq-metric">
            <b>{q.total.qualityAccuracy === undefined ? '—' : verdictFromScore(q.total.qualityAccuracy)}</b>
            <span className="aq-badge">{q.total.judged} judged</span>
            {q.total.judgedBad > 0 && (
              <Link
                className="aq-badge bad aq-link"
                to={assignmentAnalysesPath(key, { questionId: q.questionId, quality: 'bad' })}
                onClick={e => e.stopPropagation()}
              >
                {q.total.judgedBad} bad →
              </Link>
            )}
          </span>
        : <span className="aq-muted">—</span>),
    },
    {
      key: 'health',
      header: 'Health',
      render: q => <HealthSummary cell={q.total} />,
    },
    // Per-config columns only when more than one config ran: with one, they
    // repeat the aggregate columns above verbatim.
    ...(configs.length > 1 ? configs.map(config => ({
      key: config,
      header: configLabel(config),
      className: 'aq-group',
      render: (q: (typeof questions)[number]) => (
        q.cells[config] ? <CellSummary cell={q.cells[config]} /> : <span className="aq-cell-empty">—</span>
      ),
    })) : []),
  ];

  // ── Second table: the same assignment pivoted onto students ───────────
  const studentColumns: ColumnDef<(typeof students)[number]>[] = [
    {
      key: 'name',
      header: 'Student',
      mobile: 'title',
      render: s => (
        <>
          {s.name}
          {s.status !== 'active' && <div><span className="aq-badge warn">{s.status}</span></div>}
        </>
      ),
    },
    {
      key: 'questions',
      header: 'Questions analysed',
      render: s => <MetricCell value={s.questions || undefined} />,
    },
    {
      key: 'analyses',
      header: 'Analyses',
      // Exceeds "questions analysed" exactly when they retook.
      render: s => (
        <span className="aq-metric">
          <MetricCell value={s.analyses || undefined} />
          {s.attempts > 1 && <span className="aq-badge">{s.attempts} attempts</span>}
        </span>
      ),
    },
    {
      key: 'accuracy',
      header: 'Avg accuracy score',
      render: s => <MetricCell value={s.avgCorrectnessScore} />,
    },
    {
      key: 'evalScore',
      header: 'Eval score',
      render: s => (s.judged > 0
        ? <span className="aq-metric">
            <b>{s.qualityOverall === undefined ? '—' : verdictFromScore(s.qualityOverall)}</b>
            <span className="aq-badge">{s.judged} judged</span>
          </span>
        : <span className="aq-muted">—</span>),
    },
  ];

  return (
    <div className="aq-page">
      <div className="aq-breadcrumb">
        <Link to="/evals?tab=assignments">Assignments</Link> / <span>{data.assignment.title}</span>
      </div>

      <AssignmentHeader assignment={data.assignment} />

      <JudgeAction assignmentKey={key} judged={total.judged} attempts={total.attempts} />

      {total.judgedBad > 0 && (
        <p className="aq-note">
          <Link className="aq-link" to={assignmentAnalysesPath(key, { quality: 'bad' })}>
            {total.judgedBad} analys{total.judgedBad === 1 ? 'is' : 'es'} judged bad →
          </Link>
          {' '}The mean hides these: two good and one bad averages to acceptable.
        </p>
      )}

      <DataTable
        columns={questionColumns}
        data={questions}
        rowKey={q => q.questionId}
        // To the assignment's analyses, unfiltered: the row is a way in, not a
        // narrowing — the question filter is there if the reader wants it.
        onRowClick={() => navigate(assignmentAnalysesPath(key))}
        rowHref={() => assignmentAnalysesPath(key)}
        emptyMessage="Nobody has attempted this assignment yet."
      />

      <h3 className="aq-section">Students</h3>
      <DataTable
        columns={studentColumns}
        data={students}
        rowKey={s => s.studentId}
        onRowClick={s => navigate(assignmentAnalysesPath(key, { studentId: s.studentId }))}
        rowHref={s => assignmentAnalysesPath(key, { studentId: s.studentId })}
        emptyMessage="Nobody has been granted this assignment."
      />
    </div>
  );
}
